import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { PublicWebsite } from "./components/PublicWebsite";
import { PublicLandingPage } from "./components/PublicLandingPage";
import { ProjectSharePage } from "./components/ProjectSharePage";
import { PublicAnalyticsBeacon } from "./components/PublicAnalyticsBeacon";
import { useSiteFavicon } from "./hooks/useSiteFavicon";
import { CMS_PUBLIC_PATH, FRAMER_REFERENCE_PATH, legacySitePath } from "./lib/publicHome";
import "./index.css";

// The admin panel (and its PDF, charts and editor code) loads only on /admin and /auth,
// so visitors of the public site don't download it.
const App = lazy(() => import("./App").then((m) => ({ default: m.App })));
const AcceptInvite = lazy(() => import("./components/AcceptInvite").then((m) => ({ default: m.AcceptInvite })));

function SiteFavicon() {
  useSiteFavicon();
  return null;
}

/** Unknown public paths go to the home page. */
function PublicHomeFallback() {
  return <Navigate to={CMS_PUBLIC_PATH} replace />;
}

/**
 * Old /site/... links of the clone. In production the Worker answers them with a 301 and serves
 * the Framer reference at /site itself; this covers the dev server.
 */
function LegacySiteRedirect() {
  const location = useLocation();
  const target = legacySitePath(location.pathname);
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
}

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <BrowserRouter>
      <SiteFavicon />
      <PublicAnalyticsBeacon />
      <Routes>
        {/* Public website (CMS). */}
        <Route path={CMS_PUBLIC_PATH} element={<PublicWebsite />} />
        <Route path="/start" element={<Navigate to={CMS_PUBLIC_PATH} replace />} />
        <Route path="/contact" element={<PublicWebsite page="contact" />} />
        <Route path="/blog" element={<PublicWebsite page="blog" />} />
        <Route path="/blog/:slug" element={<PublicWebsite page="post" />} />
        <Route path="/solutions" element={<PublicWebsite page="solutions" />} />
        <Route path="/solutions/:slug" element={<PublicWebsite page="solution" />} />
        <Route path={FRAMER_REFERENCE_PATH} element={<LegacySiteRedirect />} />
        <Route path={`${FRAMER_REFERENCE_PATH}/*`} element={<LegacySiteRedirect />} />
        <Route path="/landing/:slug" element={<PublicLandingPage />} />
        <Route path="/p/:token" element={<ProjectSharePage />} />

        {/* Admin panel route - requires authentication */}
        <Route path="/admin/accept-invite" element={<Suspense fallback={null}><AcceptInvite /></Suspense>} />
        <Route path="/admin/*" element={<Suspense fallback={null}><App /></Suspense>} />

        {/* Auth/Backend route - alias for admin panel */}
        <Route path="/auth/*" element={<Suspense fallback={null}><App /></Suspense>} />

        <Route path="*" element={<PublicHomeFallback />} />
      </Routes>
    </BrowserRouter>
  </React.StrictMode>
);


